import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiLogOut, FiGrid, FiDownload, FiUpload } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI } from '../services/api';

// ── Styled components ──────────────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
`;

const Header = styled.header`
  background: #0a4d3a;
  color: #fff;
  padding: 0 32px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  h1 {
    font-size: 18px;
    font-weight: 600;
  }

  span {
    background: #0d6e52;
    color: #9fe8cc;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 4px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: #aaa;
`;

const LogoutBtn = styled.button`
  background: transparent;
  color: #aaa;
  border: 1px solid #1a6e56;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: color 0.2s, border-color 0.2s;

  &:hover {
    color: #fff;
    border-color: #aaa;
  }
`;

const NavBtn = styled.button`
  background: transparent;
  color: #9fe8cc;
  border: 1px solid #1a6e56;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #9fe8cc;
    color: #fff;
  }
`;

const Content = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h2 {
    font-size: 20px;
    font-weight: 600;
    color: #1a1a2e;
  }
`;

const PrimaryBtn = styled.button`
  background: #0a4d3a;
  color: #fff;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background 0.2s;

  &:hover {
    background: #083d2e;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e8e8e8;

  th {
    text-align: left;
    padding: 12px 16px;
    color: #666;
    font-weight: 500;
    border-bottom: 1px solid #eee;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  td {
    padding: 14px 16px;
    border-bottom: 1px solid #f5f5f5;
    color: #333;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  background: ${p => p.$active ? '#e6f7f1' : '#f5f5f5'};
  color: ${p => p.$active ? '#0a7c55' : '#888'};
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const IconBtn = styled.button`
  background: ${p => p.$danger ? '#fff0f0' : '#f5f5f5'};
  color: ${p => p.$danger ? '#c0392b' : '#555'};
  border-radius: 6px;
  padding: 6px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.15s;

  &:hover {
    background: ${p => p.$danger ? '#ffd5d5' : '#e8e8e8'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px;
  color: #aaa;
  font-size: 15px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8e8e8;
`;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.15);

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 20px;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;

  label {
    font-size: 13px;
    font-weight: 500;
    color: #444;
  }

  input {
    padding: 9px 12px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;

    &:focus {
      border-color: #0a4d3a;
      box-shadow: 0 0 0 3px rgba(10,77,58,0.1);
      outline: none;
    }
  }
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #444;
  cursor: pointer;

  input[type="checkbox"] {
    width: 16px;
    height: 16px;
    cursor: pointer;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 8px;
  justify-content: flex-end;
`;

const CancelBtn = styled.button`
  background: #f5f5f5;
  color: #555;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 14px;
  cursor: pointer;
  &:hover { background: #eee; }
`;

const ErrorMsg = styled.div`
  background: #fff0f0;
  border: 1px solid #ffb3b3;
  color: #c0392b;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 13px;
`;

// ── Component ──────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formError, setFormError] = useState('');

  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newFullName, setNewFullName] = useState('');
  const [editFullName, setEditFullName] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [backupPassword, setBackupPassword] = useState('');
  const [backupFile, setBackupFile] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupMessage, setBackupMessage] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const data = await adminAPI.listUsers();
      setUsers(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      await adminAPI.createUser({ email: newEmail, password: newPassword, full_name: newFullName });
      setNewEmail(''); setNewPassword(''); setNewFullName('');
      setShowCreate(false);
      loadUsers();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Error creating user');
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setEditFullName(u.full_name || '');
    setEditIsActive(u.is_active);
    setFormError('');
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setFormError('');
    try {
      await adminAPI.updateUser(editingUser.id, { full_name: editFullName, is_active: editIsActive });
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Error updating user');
    }
  };

  const handleDownloadBackup = async () => {
    if (backupPassword.length < 8) return setBackupMessage('The password must contain at least 8 characters');
    setBackupBusy(true); setBackupMessage('');
    try {
      const blob = await adminAPI.downloadBackup(backupPassword);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = 'tenant_backup.zip'; link.click();
      window.URL.revokeObjectURL(url); setBackupMessage('Backup downloaded');
    } catch (err) { setBackupMessage(err.response?.data?.detail || 'Error creating the backup'); }
    finally { setBackupBusy(false); }
  };

  const handleRestoreBackup = async () => {
    if (!backupFile) return setBackupMessage('Choose a backup file');
    if (!backupPassword) return setBackupMessage('Enter the backup password');
    if (!window.confirm('The restore will replace all company data. Continue?')) return;
    setBackupBusy(true); setBackupMessage('');
    try {
      const result = await adminAPI.restoreBackup(backupFile, backupPassword);
      setBackupMessage(`${result.message}: ${result.samples} samples, ${result.users} users`);
      loadUsers();
    } catch (err) { setBackupMessage(err.response?.data?.detail || 'Error restoring the backup'); }
    finally { setBackupBusy(false); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    await adminAPI.deleteUser(userId);
    loadUsers();
  };

  return (
    <Page>
      <Header>
        <HeaderLeft>
          <h1>Quanzico</h1>
          <span>Admin</span>
        </HeaderLeft>
        <HeaderRight>
          <NavBtn onClick={() => navigate('/')}>
            <FiGrid size={14} />
            App
          </NavBtn>
          <span>{user?.email}</span>
          <LogoutBtn onClick={logout}>
            <FiLogOut size={14} />
            Logout
          </LogoutBtn>
        </HeaderRight>
      </Header>

      <Content>
        <SectionTitle>
          <h2>Company backup</h2>
        </SectionTitle>
        <FormField>
          <label>ZIP password (min 8 characters)</label>
          <input type="password" value={backupPassword} onChange={e => setBackupPassword(e.target.value)} />
        </FormField>
        <ActionGroup>
          <PrimaryBtn onClick={handleDownloadBackup} disabled={backupBusy}><FiDownload size={15} /> Download backup</PrimaryBtn>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FiUpload size={15} /> Choose file
            <input type="file" accept=".zip,application/zip" onChange={e => setBackupFile(e.target.files[0])} style={{ display: 'none' }} />
          </label>
          <PrimaryBtn onClick={handleRestoreBackup} disabled={backupBusy || !backupFile}><FiUpload size={15} /> Restore</PrimaryBtn>
        </ActionGroup>
        {backupMessage && <ErrorMsg>{backupMessage}</ErrorMsg>}

        <SectionTitle>
          <h2>User management</h2>
          <PrimaryBtn onClick={() => { setFormError(''); setShowCreate(true); }}>
            <FiPlus size={15} />
            New user
          </PrimaryBtn>
        </SectionTitle>

        {loading ? (
          <EmptyState>Loading...</EmptyState>
        ) : users.length === 0 ? (
          <EmptyState>No users. Create the first one!</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Status</th>
                <th>Created on</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td>{u.full_name || '—'}</td>
                  <td>{u.email}</td>
                  <td>
                    <StatusBadge $active={u.is_active}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </StatusBadge>
                  </td>
                  <td>{new Date(u.date_created).toLocaleDateString('en-US')}</td>
                  <td>
                    <ActionGroup>
                      <IconBtn onClick={() => handleEdit(u)} title="Edit">
                        <FiEdit2 size={14} />
                      </IconBtn>
                      <IconBtn $danger onClick={() => handleDelete(u.id)} title="Delete">
                        <FiTrash2 size={14} />
                      </IconBtn>
                    </ActionGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Content>

      {showCreate && (
        <Modal onClick={() => setShowCreate(false)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <h3>New user</h3>
            <ModalForm onSubmit={handleCreate}>
              {formError && <ErrorMsg>{formError}</ErrorMsg>}
              <FormField>
                <label>Full name (optional)</label>
                <input
                  type="text"
                  value={newFullName}
                  onChange={e => setNewFullName(e.target.value)}
                  placeholder="John Doe"
                  autoFocus
                />
              </FormField>
              <FormField>
                <label>Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                  placeholder="user@company.com"
                  required
                />
              </FormField>
              <FormField>
                <label>Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </FormField>
              <ModalActions>
                <CancelBtn type="button" onClick={() => setShowCreate(false)}>Cancel</CancelBtn>
                <PrimaryBtn type="submit">Create user</PrimaryBtn>
              </ModalActions>
            </ModalForm>
          </ModalCard>
        </Modal>
      )}

      {editingUser && (
        <Modal onClick={() => setEditingUser(null)}>
          <ModalCard onClick={e => e.stopPropagation()}>
            <h3>Edit user</h3>
            <ModalForm onSubmit={handleUpdate}>
              {formError && <ErrorMsg>{formError}</ErrorMsg>}
              <FormField>
                <label>Email</label>
                <input type="email" value={editingUser.email} disabled style={{ background: '#f9f9f9', color: '#999' }} />
              </FormField>
              <FormField>
                <label>Full name</label>
                <input
                  type="text"
                  value={editFullName}
                  onChange={e => setEditFullName(e.target.value)}
                  placeholder="John Doe"
                  autoFocus
                />
              </FormField>
              <CheckRow>
                <input
                  type="checkbox"
                  checked={editIsActive}
                  onChange={e => setEditIsActive(e.target.checked)}
                />
                Active user
              </CheckRow>
              <ModalActions>
                <CancelBtn type="button" onClick={() => setEditingUser(null)}>Cancel</CancelBtn>
                <PrimaryBtn type="submit">Save changes</PrimaryBtn>
              </ModalActions>
            </ModalForm>
          </ModalCard>
        </Modal>
      )}
    </Page>
  );
}
